/**
 * API service for device configuration management
 */

import {
  DeviceConfig,
  DeviceConfigCreate,
  DeviceConfigUpdate,
  DeviceConfigHistory,
  DeviceConfigYAML,
  ConfigDeployResponse,
  DeploymentResult,
  DeploymentHistoryResponse,
  AllDeploymentsResponse
} from '../types/config';
import httpClient from './httpClient';

// Usa il client HTTP condiviso (inietta Authorization e gestisce refresh 401)
const api = httpClient;

export const configApi = {
  /**
   * Get current device configuration
   */
  async getConfig(deviceId: number): Promise<DeviceConfig> {
    const response = await api.get<DeviceConfig>(`/devices/${deviceId}/config`);
    return response.data;
  },

  /**
   * Create new device configuration
   */
  async createConfig(deviceId: number, config: DeviceConfigCreate): Promise<DeviceConfig> {
    const response = await api.post<DeviceConfig>(`/devices/${deviceId}/config`, config);
    return response.data;
  },

  /**
   * Update device configuration
   */
  async updateConfig(deviceId: number, config: DeviceConfigUpdate): Promise<DeviceConfig> {
    console.log('=== API UPDATE DEBUG ===');
    console.log('Sending to backend:', JSON.stringify(config, null, 2));
    console.log('URL:', `/devices/${deviceId}/config`);
    console.log('=======================');
    
    const response = await api.put<DeviceConfig>(`/devices/${deviceId}/config`, config);
    return response.data;
  },

  /**
   * Get device configuration history
   */
  async getConfigHistory(
    deviceId: number, 
    skip: number = 0, 
    limit: number = 50
  ): Promise<DeviceConfigHistory> {
    const response = await api.get<DeviceConfigHistory>(
      `/devices/${deviceId}/config/history?skip=${skip}&limit=${limit}`
    );
    return response.data;
  },

  /**
   * Deploy current configuration to device
   */
  async deployConfig(deviceId: number): Promise<ConfigDeployResponse> {
    const response = await api.post<ConfigDeployResponse>(`/devices/${deviceId}/config/deploy`);
    return response.data;
  },

  /**
   * Get deployment history for a device
   */
  async getDeploymentHistory(deviceId: number, limit: number = 10): Promise<DeploymentHistoryResponse> {
    const response = await api.get<DeploymentHistoryResponse>(`/devices/${deviceId}/config/deployments?limit=${limit}`);
    return response.data;
  },

  /**
   * Get status of a specific deployment
   */
  async getDeploymentStatus(deviceId: number, deploymentId: string): Promise<DeploymentResult> {
    const response = await api.get<DeploymentResult>(`/devices/${deviceId}/config/deployments/${deploymentId}`);
    return response.data;
  },

  /**
   * Cancel a pending deployment
   */
  async cancelDeployment(deviceId: number, deploymentId: string): Promise<void> {
    await api.post(`/devices/${deviceId}/config/deployments/${deploymentId}/cancel`);
  },

  /**
   * Get all deployments across all devices
   */
  async getAllDeployments(limit: number = 50): Promise<AllDeploymentsResponse> {
    const response = await api.get<AllDeploymentsResponse>(`/config/deployments?limit=${limit}`);
    return response.data;
  },

  /**
   * Get configuration in YAML format
   */
  async getConfigYaml(deviceId: number): Promise<DeviceConfigYAML> {
    const response = await api.get<DeviceConfigYAML>(`/devices/${deviceId}/config/yaml`);
    return response.data;
  },



  /**
   * Validate configuration data
   */
  validateConfig(config: Partial<DeviceConfigCreate>): string[] {
    const errors: string[] = [];

    // Validate OCXO control type
    if (config.ocxo_control_type && !['SPI', 'SSH'].includes(config.ocxo_control_type)) {
      errors.push('OCXO control type must be either SPI or SSH');
    }

    // Validate GNSS configuration
    if (config.gnss_count !== undefined) {
      if (config.gnss_count < 1 || config.gnss_count > 4) {
        errors.push('GNSS count must be between 1 and 4');
      }
      
      // Validate delays arrays match count
      if (config.gnss_internal_delays && config.gnss_internal_delays.length !== config.gnss_count) {
        errors.push('Number of internal delays must match GNSS count');
      }
      if (config.gnss_cable_delays && config.gnss_cable_delays.length !== config.gnss_count) {
        errors.push('Number of cable delays must match GNSS count');
      }
      
      // Validate delay values are numeric
      if (config.gnss_internal_delays) {
        config.gnss_internal_delays.forEach((delay, index) => {
          if (isNaN(delay)) {
            errors.push(`Internal delay for GNSS${index + 1} must be a valid number`);
          }
        });
      }
      if (config.gnss_cable_delays) {
        config.gnss_cable_delays.forEach((delay, index) => {
          if (isNaN(delay)) {
            errors.push(`Cable delay for GNSS${index + 1} must be a valid number`);
          }
        });
      }
    }

    // Validate controller type
    if (config.controller_type && !['kalman', 'pi'].includes(config.controller_type)) {
      errors.push('Controller type must be either "kalman" or "pi"');
    }

    // Validate numeric ranges
    const numericValidations = [
      { field: 'num_slots', value: config.num_slots, min: 1, max: 50, label: 'Number of slots' },
      { field: 'gnss_count', value: config.gnss_count, min: 1, max: 4, label: 'GNSS count' },
      { field: 'kalman_window_size', value: config.kalman_window_size, min: 1, max: 100, label: 'Kalman window size' },
    ];

    numericValidations.forEach(({ value, min, max, label }) => {
      if (value !== undefined && (value < min || value > max)) {
        errors.push(`${label} must be between ${min} and ${max}`);
      }
    });

    // Validate PI gains are positive
    if (config.pi_kp !== undefined && config.pi_kp < 0) {
      errors.push('PI proportional gain must be positive');
    }
    if (config.pi_ki !== undefined && config.pi_ki < 0) {
      errors.push('PI integral gain must be positive');
    }

    // Validate notes length
    if (config.notes && config.notes.length > 1000) {
      errors.push('Notes must be less than 1000 characters');
    }

    return errors;
  }
};

export default configApi; 